import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SystemPerformance = () => {
  const performanceData = [
    { time: '00:00', responseTime: 120, requests: 450, bandwidth: 2.3 },
    { time: '04:00', responseTime: 95, requests: 320, bandwidth: 1.8 },
    { time: '08:00', responseTime: 180, requests: 890, bandwidth: 4.2 },
    { time: '12:00', responseTime: 210, requests: 1200, bandwidth: 5.8 },
    { time: '16:00', responseTime: 165, requests: 980, bandwidth: 4.5 },
    { time: '20:00', responseTime: 145, requests: 720, bandwidth: 3.6 }
  ];

  const systemMetrics = [
    { label: 'Tiempo de Respuesta Promedio', value: '165ms', status: 'good', icon: 'Zap', color: 'text-green-600' },
    { label: 'Solicitudes por Minuto', value: '847', status: 'good', icon: 'Activity', color: 'text-blue-600' },
    { label: 'Uso de Ancho de Banda', value: '3.8 GB', status: 'normal', icon: 'Database', color: 'text-orange-600' },
    { label: 'Tasa de Error', value: '0.02%', status: 'good', icon: 'AlertCircle', color: 'text-green-600' }
  ];

  return (
    <div className="space-y-6">
      {/* System Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {systemMetrics?.map((metric, index) => (
          <div key={index} className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{metric?.label}</p>
                <p className="text-2xl font-bold text-foreground">{metric?.value}</p>
              </div>
              <Icon name={metric?.icon} size={32} className={metric?.color} />
            </div>
          </div>
        ))}
      </div>

      {/* Response Time Chart */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
          <Icon name="TrendingUp" size={24} className="mr-2 text-primary" />
          Tiempo de Respuesta del Servidor (Últimas 24 horas)
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={performanceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="responseTime" stroke="#3b82f6" name="Tiempo de Respuesta (ms)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Bandwidth Usage */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Database" size={24} className="mr-2 text-primary" />
          Uso de Ancho de Banda
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={performanceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="bandwidth" stroke="#f59e0b" fill="#fbbf24" name="Ancho de Banda (GB)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Server" size={24} className="mr-2 text-primary" />
            Estado del Servidor
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <span className="text-sm font-medium text-foreground">Servidor Principal</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operativo</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <span className="text-sm font-medium text-foreground">Base de Datos</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operativo</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <span className="text-sm font-medium text-foreground">Almacenamiento</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operativo</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <span className="text-sm font-medium text-foreground">CDN</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operativo</span>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
            <Icon name="HardDrive" size={24} className="mr-2 text-primary" />
            Recursos del Sistema
          </h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">CPU</span>
                <span className="text-sm text-muted-foreground">45%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '45%' }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Memoria RAM</span>
                <span className="text-sm text-muted-foreground">62%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: '62%' }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Almacenamiento</span>
                <span className="text-sm text-muted-foreground">38%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-orange-600 h-2 rounded-full" style={{ width: '38%' }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Red</span>
                <span className="text-sm text-muted-foreground">28%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-purple-600 h-2 rounded-full" style={{ width: '28%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemPerformance;
